import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { 
  Shield, 
  CheckCircle, 
  ClipboardCheck, 
  BookOpen, 
  Users, 
  LineChart, 
  FileText,
  Settings,
  BarChart2,
  MessageCircle,
  Home
} from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";

const Compliance: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [projetosDialogOpen, setProjetosDialogOpen] = React.useState(false);

  const isActivePath = (path: string): boolean => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    if (path === 'projects') {
      return ['/my-projects', '/project/status', '/project/region', '/all-projects'].some(
        p => location.pathname.startsWith(p)
      );
    }
    return location.pathname === path;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleProjetosClick = () => {
    setProjetosDialogOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <UserHeader />
      {/* Menu horizontal superior */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex space-x-6 overflow-x-auto py-3">
          <button 
            className={`${isActivePath('/planos') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/planos')}
          >
            Planos
          </button>
          <button 
            className={`${isActivePath('/dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`${isActivePath('projects') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={handleProjetosClick}
          >
            Projetos
          </button>
          <button 
            className={`${isActivePath('/emission') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/emission')}
          >
            Emissão
          </button>
          <button 
            className={`${isActivePath('/traceability') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/traceability')}
          >
            Rastreabilidade
          </button>
          <button 
            className={`${isActivePath('/certificates') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/certificates')}
          >
            Certificados
          </button>
          <button
            className={`${isActivePath('/pregao-dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/pregao-dashboard')}
          >
            Bolsa de Crédito de Carbono Voluntário
          </button>
          <button 
            className={`${isActivePath('/compliance') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/compliance')}
          >
            Conformidade
          </button>
          <button 
            className={`${isActivePath('/retirement') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/retirement')}
          >
            Aposentadoria
          </button>
          <button 
            className={`${isActivePath('/educational') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/educational')}
          >
            Espaço Educativo
          </button>
        </div>
      </div>
      {/* Pop-up de Acesso para Projetos */}
      <Dialog open={projetosDialogOpen} onOpenChange={setProjetosDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Acesso a Projetos</DialogTitle>
            <DialogDescription className="text-center">
              Selecione o tipo de visualização de projetos que deseja acessar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/my-projects');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Meus Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Visualize todos os seus projetos cadastrados</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/status');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Status dos Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acompanhe o status de todos os projetos</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/region');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Projetos por Região</h3>
                <p className="text-sm text-gray-500 text-center">Explore projetos por localização geográfica</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              window.open('https://banclima.com/projetos', '_blank');
              setProjetosDialogOpen(false);
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Portal de Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acesse o portal público de projetos do Banclima</p>
              </CardContent>
            </Card>
          </div>
          <DialogFooter className="sm:justify-center">
            <Button 
              variant="outline" 
              onClick={() => setProjetosDialogOpen(false)}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="container mx-auto px-4 py-6 flex-grow">
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="h-8 w-8 text-[#0e7cb4]" />
            <h1 className="text-2xl font-bold text-[#0e7cb4]">Conformidade (Compliance) de Créditos de Carbono</h1>
          </div>
          
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
            <p className="text-gray-700">
              A <strong>ISO 14064</strong> é uma norma internacional que fornece diretrizes para a quantificação, 
              monitoramento, verificação e declaração das emissões de gases de efeito estufa (GEE), sendo essencial 
              para a gestão de créditos de carbono. Para estruturar um setor de compliance de créditos de carbono 
              de acordo com a ISO 14064, é importante que a organização siga uma série de etapas que garantam a 
              conformidade com a norma e maximizem a transparência e a confiabilidade dos registros.
            </p>
          </div>
          
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Estruturação de um Setor de Compliance</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Etapa 1: Designação de Responsáveis */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg text-[#0e7cb4] flex items-center">
                    <Users className="h-5 w-5 mr-2" />
                    Designação de Responsáveis
                  </CardTitle>
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">Etapa 1</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Equipe de Compliance:</strong> Formação de uma equipe dedicada, 
                    com profissionais especializados em gestão ambiental, auditorias e regulamentos de créditos de carbono.</p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Liderança e Governança:</strong> Envolvimento da alta gestão, 
                    garantindo que o compliance com a ISO 14064 seja uma prioridade e esteja alinhado com a estratégia organizacional.</p>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Etapa 2: Mapeamento e Avaliação */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg text-[#0e7cb4] flex items-center">
                    <LineChart className="h-5 w-5 mr-2" />
                    Mapeamento e Avaliação
                  </CardTitle>
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">Etapa 2</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Levantamento de Processos:</strong> Identificação de todos os processos 
                    organizacionais relacionados às emissões de GEE e à geração de créditos de carbono.</p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Avaliação de Riscos:</strong> Análise de riscos para entender as áreas 
                    mais críticas em termos de conformidade com a ISO 14064.</p>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Etapa 3: Definição de Políticas e Procedimentos */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg text-[#0e7cb4] flex items-center">
                    <ClipboardCheck className="h-5 w-5 mr-2" />
                    Políticas e Procedimentos
                  </CardTitle>
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">Etapa 3</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Política Ambiental:</strong> Criação ou revisão de uma política ambiental 
                    alinhada aos requisitos da ISO 14064, incluindo compromissos de redução das emissões e gestão de créditos.</p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Procedimentos de Quantificação:</strong> Definição de procedimentos para 
                    quantificação das emissões de GEE com base nas diretrizes da norma.</p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Procedimentos de Verificação:</strong> Desenvolvimento de métodos claros para 
                    a verificação e validação das emissões de GEE e dos créditos de carbono gerados.</p>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Etapa 4: Implementação de Sistema de Monitoramento */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg text-[#0e7cb4] flex items-center">
                    <BarChart2 className="h-5 w-5 mr-2" />
                    Monitoramento e Registro
                  </CardTitle>
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">Etapa 4</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Plataforma de Monitoramento:</strong> Implementação ou integração de uma 
                    plataforma para o monitoramento contínuo das emissões de GEE, que possibilite a geração de relatórios consistentes.</p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Processos de Registro:</strong> Estabelecimento de um processo robusto de 
                    registro de créditos de carbono gerados e transacionados, garantindo a rastreabilidade e a conformidade.</p>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Etapa 5: Treinamento e Capacitação */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg text-[#0e7cb4] flex items-center">
                    <BookOpen className="h-5 w-5 mr-2" />
                    Treinamento e Capacitação
                  </CardTitle>
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">Etapa 5</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Treinamento Contínuo:</strong> Organização de treinamentos periódicos para a equipe 
                    interna e fornecedores envolvidos no processo de geração de créditos de carbono, garantindo atualização sobre a ISO 14064.</p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Capacitação sobre Auditoria:</strong> Oferta de capacitação em auditorias ambientais 
                    internas, assegurando que o setor de compliance esteja apto a realizar verificações internas.</p>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Etapa 6: Auditorias */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg text-[#0e7cb4] flex items-center">
                    <ClipboardCheck className="h-5 w-5 mr-2" />
                    Auditorias Internas e Externas
                  </CardTitle>
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">Etapa 6</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Auditoria Interna:</strong> Realização de auditorias internas regulares para 
                    verificar a conformidade com os procedimentos da ISO 14064 e identificar eventuais falhas no processo.</p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Auditoria Externa:</strong> Garantia de que as auditorias externas sejam feitas 
                    por entidades certificadoras acreditadas para realizar as verificações conforme os requisitos da norma.</p>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Etapa 7: Documentação e Relatórios */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg text-[#0e7cb4] flex items-center">
                    <FileText className="h-5 w-5 mr-2" />
                    Documentação e Relatórios
                  </CardTitle>
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">Etapa 7</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Relatórios de Emissões e Créditos:</strong> Preparação de relatórios claros 
                    e completos sobre as emissões de GEE e os créditos de carbono, garantindo que todos os dados sejam precisos.</p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Documentação de Processos:</strong> Manutenção de registros detalhados de 
                    todos os processos de quantificação, verificação e comercialização de créditos de carbono.</p>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Etapa 8: Ajustes e Melhoria Contínua */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg text-[#0e7cb4] flex items-center">
                    <Settings className="h-5 w-5 mr-2" />
                    Ajustes e Melhoria Contínua
                  </CardTitle>
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">Etapa 8</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Análise de Resultados:</strong> Utilização dos resultados das auditorias e 
                    relatórios de conformidade para ajustar processos e políticas.</p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Melhoria Contínua:</strong> Incentivo à melhoria contínua por meio da revisão 
                    periódica dos procedimentos e da incorporação de feedbacks para fortalecer o setor de compliance.</p>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Etapa 9: Engajamento com Stakeholders */}
            <Card className="md:col-span-2">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg text-[#0e7cb4] flex items-center">
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Engajamento com Stakeholders
                  </CardTitle>
                  <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">Etapa 9</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Transparência com Partes Interessadas:</strong> Manutenção dos stakeholders, 
                    como clientes, reguladores e investidores, informados sobre as práticas de geração e comercialização de créditos de carbono.</p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700"><strong>Relatórios Públicos:</strong> Disponibilização de relatórios públicos sobre 
                    as emissões de GEE e a gestão de créditos de carbono para garantir a confiança e transparência.</p>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <Tabs defaultValue="iso14064" className="w-full">
          <TabsList className="w-full bg-white border-b border-gray-200 p-0">
            <TabsTrigger value="iso14064" className="flex-1 py-3">ISO 14064</TabsTrigger>
            <TabsTrigger value="benefits" className="flex-1 py-3">Benefícios do Compliance</TabsTrigger>
            <TabsTrigger value="implementation" className="flex-1 py-3">Implementação</TabsTrigger>
            <TabsTrigger value="challenges" className="flex-1 py-3">Desafios Comuns</TabsTrigger>
          </TabsList>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <TabsContent value="iso14064" className="mt-0">
              <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Sobre a ISO 14064</h2>
              <p className="text-gray-700 mb-4">
                A ISO 14064 é composta por três partes, cada uma abordando diferentes aspectos da gestão de emissões de gases de efeito estufa:
              </p>
              
              <div className="space-y-6">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">ISO 14064-1</h3>
                  <p className="text-gray-700">
                    Especifica os princípios e requisitos no nível organizacional para quantificação e elaboração de relatórios 
                    de emissões e remoções de GEE. Inclui requisitos para o design, desenvolvimento, gestão, relatórios e verificação 
                    de inventários de GEE de uma organização.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">ISO 14064-2</h3>
                  <p className="text-gray-700">
                    Detalha os princípios e requisitos para a determinação de linhas de base de projetos de GEE e para o monitoramento, 
                    quantificação e relato de atividades de projetos. Esta parte é especialmente relevante para projetos de crédito de carbono,
                    pois fornece a estrutura para a quantificação das reduções de emissões ou aumento das remoções de GEE.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">ISO 14064-3</h3>
                  <p className="text-gray-700">
                    Especifica os princípios e requisitos para verificação e validação das declarações de GEE. Fornece diretrizes 
                    para os processos de validação ou verificação de inventários de GEE, projetos de GEE e suas reivindicações.
                  </p>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="benefits" className="mt-0">
              <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Benefícios do Compliance em Créditos de Carbono</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="flex items-start p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="bg-green-100 p-2 rounded-full mr-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-800">Credibilidade Amplificada</h3>
                    <p className="text-gray-700 mt-1">
                      Aumenta a confiança das partes interessadas na veracidade e precisão dos créditos de carbono gerados, 
                      atraindo mais investidores e parceiros de negócios.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="bg-green-100 p-2 rounded-full mr-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-800">Mitigação de Riscos</h3>
                    <p className="text-gray-700 mt-1">
                      Reduz os riscos regulatórios e reputacionais associados a declarações imprecisas ou fraudulentas 
                      sobre emissões e compensações de carbono.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="bg-green-100 p-2 rounded-full mr-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-800">Acesso a Mercados</h3>
                    <p className="text-gray-700 mt-1">
                      Facilita a entrada em mercados regulados e voluntários de carbono que exigem altos padrões 
                      de conformidade e transparência.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="bg-green-100 p-2 rounded-full mr-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-800">Eficiência Operacional</h3>
                    <p className="text-gray-700 mt-1">
                      Processos padronizados de monitoramento e relatório levam a maior eficiência operacional e 
                      melhor gestão de recursos.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="implementation" className="mt-0">
              <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Implementação do Compliance</h2>
              <p className="text-gray-700 mb-4">
                A implementação eficaz de um sistema de compliance para créditos de carbono requer uma abordagem sistemática e comprometimento organizacional.
                Seguindo estas práticas recomendadas, sua organização pode estabelecer um sistema robusto que garanta a conformidade com a ISO 14064:
              </p>
              
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Abordagem Faseada</h3>
                  <p className="text-gray-700">
                    Implemente o sistema de compliance de forma gradual, começando com uma avaliação inicial do estado atual, 
                    seguida por melhorias incrementais e expansão do escopo ao longo do tempo. Isso permite que a organização 
                    aprenda e se adapte durante o processo.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Integração com Sistemas Existentes</h3>
                  <p className="text-gray-700">
                    Para minimizar a duplicação de esforços e maximizar a eficiência, integre as práticas de compliance da ISO 14064 
                    aos sistemas de gestão ambiental existentes e outras estruturas de compliance da organização, como ISO 14001 ou ISO 9001.
                  </p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Utilização de Tecnologia</h3>
                  <p className="text-gray-700">
                    Empregue soluções de software especializadas para automatizar a coleta de dados, o monitoramento, a análise e a geração de relatórios. 
                    Tecnologias como IoT, blockchain e IA podem melhorar significativamente a precisão e a eficiência dos processos de compliance.
                  </p>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="challenges" className="mt-0">
              <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Desafios Comuns</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h3 className="text-lg font-medium text-red-800 mb-2">Complexidade Técnica</h3>
                  <p className="text-red-700">
                    A quantificação precisa das emissões de GEE pode ser tecnicamente desafiadora, especialmente para organizações com 
                    operações complexas ou diversificadas. Isso requer expertise técnica significativa e metodologias robustas.
                  </p>
                  <p className="text-red-700 mt-2">
                    <strong>Solução:</strong> Investir em treinamento especializado e considerar a contratação de consultores externos 
                    com experiência em metodologias de quantificação de GEE para as fases iniciais do projeto.
                  </p>
                </div>
                
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h3 className="text-lg font-medium text-red-800 mb-2">Disponibilidade e Qualidade dos Dados</h3>
                  <p className="text-red-700">
                    Muitas organizações enfrentam dificuldades na obtenção de dados completos e precisos sobre suas emissões, 
                    especialmente aquelas com cadeias de suprimentos complexas ou operações globais.
                  </p>
                  <p className="text-red-700 mt-2">
                    <strong>Solução:</strong> Implementar sistemas robustos de coleta de dados, estabelecer procedimentos claros 
                    para gerenciamento da qualidade dos dados e utilizar estimativas conservadoras quando necessário.
                  </p>
                </div>
                
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h3 className="text-lg font-medium text-red-800 mb-2">Mudanças Regulatórias</h3>
                  <p className="text-red-700">
                    O cenário regulatório relacionado às mudanças climáticas está em constante evolução, o que pode tornar 
                    desafiador manter-se atualizado e em conformidade com todos os requisitos.
                  </p>
                  <p className="text-red-700 mt-2">
                    <strong>Solução:</strong> Monitorar continuamente as mudanças regulatórias, participar de grupos setoriais relevantes 
                    e manter um sistema de gestão de compliance flexível que possa se adaptar rapidamente a novos requisitos.
                  </p>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>
      
      <Footer />
    </div>
  );
};

export default Compliance; 